import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Header } from './components/Header';
import { Pda, PdaScanEvent } from './components/Pda';
import { Input, Button, Tag, Divider, Toast } from 'antd-mobile';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { ScanningOutline } from 'antd-mobile-icons';
import { gql, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { isTrackingNumber, showTrackingNumberError } from '../../utils/BarCodeUtil';

// 根据扫描输入查询订单（支持内部运单号、渠道订单号、外部运单号）
const GET_ORDER_BY_SCAN_INPUT = gql`
  query GetOrderByScanInput($scanValue: String!) {
    getOrderByScanInput(scanValue: $scanValue) {
      id
      trackingNumber
      overallStatus
      sortingCode
    }
  }
`;

// Central订单详情查询：获取订单事件
const GET_ORDER_DETAIL_FOR_CENTRAL = gql`
  query GetOrderDetailForCentral($orderId: Long!) {
    getOrderDetailForCentral(orderId: $orderId) {
      orderId
      orderWithAddresses {
        order {
          channelId,
          realTimeSortingCode,
          returnSortingCode
        }
      }
      orderEvents {
        time
        type
        details
        failReason
        nextAction
        exceptionReasonCode
        channelId
        channelApiVersion
        isDestHubArrived
        scStages
        isFirstLmArrival
        messageKey
        originalEventType
      }
    }
  }
`;

// Channel查询：根据channelId获取渠道信息
const GET_CHANNEL = gql`
  query ChannelQuery($id: Long!) {
    getChannel(id: $id) {
      id
      channelName
    }
  }
`;

// 按异常原因码查询异常字典，获取多语言 secondaryType 作为 Reason 展示
const GET_EXCEPTION_DICTIONARY_BY_REASON_CODE = gql`
  query ExceptionDictionaryQuery($exceptionReasonCode: String!) {
    getExceptionDictionaryByReasonCode(exceptionReasonCode: $exceptionReasonCode) {
      exceptionReasonCode
      secondaryTypeZh
      secondaryTypeEn
      secondaryTypeEs
    }
  }
`;


const PageBody = () => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const [currentOrder, setCurrentOrder] = useState<any>(undefined);
  const [currentOrderDetail, setCurrentOrderDetail] = useState<any>(undefined);
  const [exceptionDictMap, setExceptionDictMap] = useState<Map<string, any>>(new Map());
  const [channelMap, setChannelMap] = useState<Map<string, any>>(new Map());
  
  const [fetchOrderByTracking, { loading }] = useLazyQuery(GET_ORDER_BY_SCAN_INPUT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const order = data?.getOrderByScanInput || null;
      setCurrentOrder(order);

      // 如果查询成功但订单不存在，显示提示
      if (!order) {
        Toast.show({
          content: t('errors.business.order.notExist'),
          position: 'bottom'
        });
      }
    },
    onError: (error) => {
      // 处理GraphQL错误，显示订单不存在提示
      Toast.show({
        content: t('errors.business.order.notExist'),
        position: 'bottom'
      });
    }
  });
  const [fetchOrderDetail] = useLazyQuery(GET_ORDER_DETAIL_FOR_CENTRAL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setCurrentOrderDetail(data?.getOrderDetailForCentral);
    },
    onError: (error) => {
    }
  });
  
  // Channel查询hook
  const [fetchChannel] = useLazyQuery(GET_CHANNEL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.getChannel) {
        const channel = data.getChannel;
        setChannelMap(prev => new Map(prev).set(channel.id, channel));
      }
    },
    onError: (error) => {
    }
  });
  const [fetchExceptionDict] = useLazyQuery(GET_EXCEPTION_DICTIONARY_BY_REASON_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const dict = data?.getExceptionDictionaryByReasonCode;
      if (dict && dict.exceptionReasonCode) {
        setExceptionDictMap(prev => new Map(prev.set(dict.exceptionReasonCode, dict)));
      }
    }
  });

  const trackingNumber = currentOrder?.trackingNumber || '';
  const status = currentOrder?.overallStatus || '';

  // 格式化状态：使用国际化
  const formatStatus = (status: string) => {
    if (!status) return '';
    
    // 尝试获取国际化翻译
    const i18nKey = `enum.overallStatus.${status}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回原始状态
    return translated === i18nKey ? status : translated;
  };

  // 格式化下一步操作：使用国际化
  const formatNextAction = (nextAction: string) => {
    if (!nextAction) return '';
    
    // 直接使用原始格式匹配配置
    const i18nKey = `config.nextAction.${nextAction}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回原始值
    return translated === i18nKey ? nextAction : translated;
  };

  // 三段码展示：优先用 order.sortingCode 的前三段拼接
  const sortingCodeCombined = useMemo(() => {
    if(currentOrderDetail?.orderWithAddresses?.order?.realTimeSortingCode) {
      return currentOrderDetail?.orderWithAddresses?.order?.realTimeSortingCode;
    }
    const sc = currentOrder?.sortingCode;
    if (sc && sc.length > 0) {
      const [s1 = '', s2 = '', s3 = ''] = sc.split('-');
      const joined = [s1, s2, s3].filter(Boolean).join('-');
      return joined || '-';
    }
    return '-';
  }, [currentOrder, currentOrderDetail]);

  // 所有事件：来自 orderDetail.orderEvents，后端按 event ID 升序返回，翻转为倒序展示
  const allEvents = useMemo(() => {
    const events = currentOrderDetail?.orderEvents || [];
    if (events.length === 0) return [];
    return [...events].reverse();
  }, [currentOrderDetail]);

  // 格式化时间：将秒时间戳转换为可读格式
  const formatTime = (time: number) => {
    if (!time || Number.isNaN(time)) return '-';
    const d = new Date(time * 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // 格式化事件标题：使用国际化
  const formatEventTitle = (type: string) => {
    if (!type) return '-';
    
    // 去掉 "OrderEventData" 前缀，获取实际的事件类型
    const key = 'OrderEventData';
    const idx = type.indexOf(key);
    let eventType = type;
    if (idx >= 0) {
      eventType = type.substring(idx + key.length).replace(/^[:\s-]+/, '') || type;
    }
    
    // 尝试获取国际化翻译，如果没有找到则返回原始类型
    const i18nKey = `enum.orderEvent.${eventType}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回处理后的原始类型
    return translated === i18nKey ? eventType : translated;
  };

  // 获取 by 信息：从 details 中以 "by " 开头的项，对于null-null情况显示channelName+版本号
  const getByInfo = (event: any) => {
    // 先从 details 中获取 by 信息
    const details = event.details || [];
    const byItem = details.find((line: string) => /^by\s+/i.test(line));
    const byValue = byItem ? byItem.replace(/^by\s+/i, '') : '';
    
    // 如果 by 值是 null-null 格式，使用渠道信息
    if (byValue === 'null-null' || 
        byValue.trim() === '' || 
        /^null[\-\s]*null$/i.test(byValue)) {
      
      const channelId = currentOrderDetail?.orderWithAddresses?.order?.channelId;
      const channelApiVersion = event.channelApiVersion;
      
      if (channelId && channelApiVersion) {
        const channel = channelMap.get(channelId.toString());
        if (channel) {
          // 显示为 channelName-V{version} 格式
          return `${channel.channelName}-V${channelApiVersion}`;
        }
      }
      
      // 如果没有渠道信息，返回系统
      return '系统';
    }
    
    // 否则返回原始的 by 值
    return byValue || '-';
  };

  // 查询异常字典：为所有不同的异常原因代码查询字典
  useEffect(() => {
    const uniqueReasonCodes = new Set<string>();
    
    allEvents.forEach(event => {
      if (event.exceptionReasonCode && 
          typeof event.exceptionReasonCode === 'string' && 
          event.exceptionReasonCode.trim().length > 0) {
        uniqueReasonCodes.add(event.exceptionReasonCode);
      }
    });
    
    // 查询还没有缓存的异常字典
    uniqueReasonCodes.forEach(code => {
      if (!exceptionDictMap.has(code)) {
        fetchExceptionDict({ variables: { exceptionReasonCode: code } });
      }
    });
  }, [allEvents, exceptionDictMap]);

  // 查询Channel信息：当有create状态事件时自动查询
  useEffect(() => {
    const createEvents = allEvents.filter(event => 
      event.type === 'OrderEventDataCreated'
    );
    
    if (createEvents.length > 0 && currentOrderDetail?.orderWithAddresses?.order?.channelId) {
      const orderChannelId = currentOrderDetail.orderWithAddresses.order.channelId;
      if (!channelMap.has(orderChannelId.toString())) {
        fetchChannel({ variables: { id: orderChannelId } });
      }
    }
  }, [allEvents, currentOrderDetail, channelMap]);

  // 获取显示用的失败原因：优先使用对应的异常字典，其次使用 failReason
  const getDisplayReason = (event: any) => {
    // 如果有异常原因代码且查询到了对应的异常字典，根据当前语言选择对应字段
    if (event.exceptionReasonCode && exceptionDictMap.has(event.exceptionReasonCode)) {
      const exceptionDict = exceptionDictMap.get(event.exceptionReasonCode);
      const currentLang = i18n.language || 'en';
      
      // 根据语言选择对应字段 - 支持更多中文语言代码
      if (currentLang.startsWith('zh')) {
        return exceptionDict.secondaryTypeZh || exceptionDict.secondaryTypeEn || '-';
      } else if (currentLang.startsWith('es')) {
        return exceptionDict.secondaryTypeEs || exceptionDict.secondaryTypeEn || '-';
      } else {
        // 默认英文
        return exceptionDict.secondaryTypeEn || exceptionDict.secondaryTypeZh || '-';
      }
    }
    // LogScan 事件使用 messageKey 国际化翻译
    if (event.messageKey) {
      const messageTranslated = t(`${event.messageKey}.message`, event.messageKey);
      if (messageTranslated !== event.messageKey) return messageTranslated;
      const translated = t(event.messageKey);
      if (typeof translated === 'string' && translated !== event.messageKey) return translated;
    }
    // 否则使用 failReason
    return event.failReason || '-';
  };

  // 缓存每个事件的显示原因，避免重复计算
  const eventsWithDisplayReason = useMemo(() => {
    return allEvents.map(event => ({
      ...event,
      displayReason: getDisplayReason(event)
    }));
  }, [allEvents, exceptionDictMap]);

  // 当 orderId 可用时，自动查询详情以获取轨迹
  useEffect(() => {
    const orderId = currentOrder?.id; // 使用 id 字段而不是 orderId
    if (orderId) {
      fetchOrderDetail({ variables: { orderId } });
    }
  }, [currentOrder?.id]);

  const onProcess = useCallback(async (event: PdaScanEvent) => {
    const code = event.code;
    if (!isTrackingNumber(code)) {
      showTrackingNumberError(code);
      return;
    }
    setKeyword(code);
    const v = code.trim();
    if (v) {
      // 每次搜索先清空上一次结果，避免展示旧数据
      setCurrentOrder(undefined);
      setCurrentOrderDetail(undefined);
      setExceptionDictMap(new Map());
      await fetchOrderByTracking({
        variables: { scanValue: v }
      });
    }
  }, [fetchOrderByTracking]);

  useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const onSearch = async () => {
    const v = keyword.trim();
    if (!v) {
      Toast.show({ content: t('pda.GlobalSearchPage.inputPlaceholder'), position: 'bottom' });
      return;
    }
    if (!isTrackingNumber(v)) {
      showTrackingNumberError(v);
      return;
    }
    
    try {
      // 每次搜索先清空上一次结果，避免展示旧数据
      setCurrentOrder(undefined);
      setCurrentOrderDetail(undefined);
      setExceptionDictMap(new Map());
      await fetchOrderByTracking({
        variables: { scanValue: v }
      });
    } catch (e) {
      Toast.show({ content: t('pda.GlobalSearchPage.searchFailed'), position: 'bottom' });
    }
  };

  return (
    <>
      {/* 顶部输入框（带扫描图标）*/}
      <div className="flex items-center mb-2 border rounded">
        <Input
          className="flex-1 p-2"
          placeholder={t('pda.GlobalSearchPage.placeholder') || 'SWX123456789'}
          value={keyword}
          onChange={setKeyword}
          onEnterPress={onSearch}
        />
        <Button
          fill={"none"}
          className="ml-2 bg-transparent border-0"
          onClick={onSearch}
        >
          <ScanningOutline fontSize={24} />
        </Button>
      </div>

      {/* 结果卡片（使用查询结果渲染） */}
      {currentOrder && (
        <div className="bg-white mt-2 rounded-lg shadow-sm border">
          {/* 第一行：运单号 */}
          <div className="px-3 py-3 flex items-center justify-center">
            <div className="font-bold text-lg">{trackingNumber}</div>
          </div>
          <Divider className="global-search-divider" />

          {/* 第二行：状态标签 */}
          {!!status && (
            <div className="px-3 py-3 flex justify-center">
              <Tag
                className="global-search-status-tag"
              >
                {formatStatus(status)}
              </Tag>
            </div>
          )}
          <Divider className="global-search-divider" />

          {/* 第三行：三段码 */}
          <div className="px-3 py-3 flex items-center justify-center relative">
            <div className="absolute left-1 top-1 text-sm text-gray-500">FWD</div>
            <div className="text-xl font-bold text-green-600">{sortingCodeCombined}</div>
          </div>
          <Divider className="global-search-divider" />

          {/* 第四行：退件四段码 */}
          {currentOrderDetail?.orderWithAddresses?.order?.returnSortingCode !== null && currentOrderDetail?.orderWithAddresses?.order?.returnSortingCode !== undefined && (
            <>
              <div className="px-3 py-3 flex items-center justify-center relative">
                <div className="absolute left-1 top-1 text-sm text-gray-500">RTN</div>
                <div className="text-xl font-bold text-red-600 min-h-[1.5rem]">{currentOrderDetail.orderWithAddresses.order.returnSortingCode || ''}</div>
              </div>
              <Divider className="global-search-divider"></Divider>
            </>
          )}

          {/* 所有事件列表 */}
          <div className="px-3 py-3">
            <div className="text-lg font-bold mb-3">{t('pda.pages.GlobalSearchPage.eventHistory') || '事件历史'}</div>
            {allEvents.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                {t('pda.pages.GlobalSearchPage.noEvents') || '暂无事件记录'}
              </div>
            ) : (
              <div className="space-y-3">
                {eventsWithDisplayReason.map((event, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-lg font-bold mb-2">{formatEventTitle(event.type)}</div>
                    <div className="text-sm text-gray-700 mb-1">{formatTime(Number(event.time))}</div>
                    <div className="text-sm text-gray-700 mb-1">
                      {t('pda.pages.GlobalSearchPage.by')} {getByInfo(event)}
                    </div>
                    
                    {/* 揽收扫描事件的特殊展示 */}
                    {event.type === 'OrderEventDataPickupArrivalScan' && (event.isDestHubArrived === true || event.scStages?.length || event.isFirstLmArrival) && (
                      <div className="text-sm text-gray-700 mb-1 space-y-1">
                        {event.isDestHubArrived === true && (
                          <div>{t('pda.pages.GlobalSearchPage.isDestHubArrived')}: {t('pda.common.yes')}</div>
                        )}
                        {event.scStages && event.scStages.length > 0 && (
                          <div>{t('pda.pages.GlobalSearchPage.scStages')}: {event.scStages.map((s: string) => t(`pda.pages.GlobalSearchPage.scStageNames.${s}`, s)).join(', ')}</div>
                        )}
                        {event.isFirstLmArrival && (
                          <div>{t('pda.pages.GlobalSearchPage.isFirstLmArrival')}: {t('pda.common.yes')}</div>
                        )}
                      </div>
                    )}

                    {/* 中转扫描事件的特殊展示 */}
                    {event.type === 'OrderEventDataTransferArrivalScan' && (event.isDestHubArrived === true || event.sortingMachineCode || event.scStages?.length || event.isFirstLmArrival) && (
                      <div className="text-sm text-gray-700 mb-1 space-y-1">
                        {event.isDestHubArrived === true && (
                          <div>{t('pda.pages.GlobalSearchPage.isDestHubArrived')}: {t('pda.common.yes')}</div>
                        )}
                        {event.scStages && event.scStages.length > 0 && (
                          <div>{t('pda.pages.GlobalSearchPage.scStages')}: {event.scStages.map((s: string) => t(`pda.pages.GlobalSearchPage.scStageNames.${s}`, s)).join(', ')}</div>
                        )}
                        {event.isFirstLmArrival && (
                          <div>{t('pda.pages.GlobalSearchPage.isFirstLmArrival')}: {t('pda.common.yes')}</div>
                        )}
                        {event.sortingMachineCode && (
                          <div>{t('pda.pages.GlobalSearchPage.sortingMachineCode')}: {event.sortingMachineCode}</div>
                        )}
                      </div>
                    )}
                    
                    {/* 其他含 scStages 的事件展示 */}
                    {['OrderEventDataArrivalScan', 'OrderEventDataBaggingScan', 'OrderEventDataSortingScan', 'OrderEventDataDepartureScan', 'OrderEventDataTransportDepartureScan', 'OrderEventDataHandedOver'].includes(event.type) && event.scStages?.length > 0 && (
                      <div className="text-sm text-gray-700 mb-1 space-y-1">
                        <div>{t('pda.pages.GlobalSearchPage.scStages')}: {event.scStages.map((s: string) => t(`pda.pages.GlobalSearchPage.scStageNames.${s}`, s)).join(', ')}</div>
                      </div>
                    )}

                    {/* LogScan 事件展示原始事件类型 */}
                    {event.type === 'OrderEventDataLogScan' && event.originalEventType && (
                      <div className="text-sm text-gray-700 mb-1">
                        {t('pda.pages.GlobalSearchPage.originalEventType')}: {formatEventTitle(event.originalEventType)}
                      </div>
                    )}

                    {/* 如果是created状态的事件，不显示reason和nextAction */}
                    {event.type !== 'OrderEventDataCreated' && (
                      <>
                        {event.displayReason && event.displayReason !== '-' && (
                          <div className="text-sm text-gray-700 mb-1">{t('pda.pages.GlobalSearchPage.reason')}: {event.displayReason}</div>
                        )}
                        {event.nextAction && event.nextAction !== '-' && (
                          <div className="text-sm text-gray-700">{t('pda.pages.GlobalSearchPage.nextAction')}: {formatNextAction(event.nextAction)}</div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 加载提示 */}
      {loading && (
        <div className="py-2 text-sm text-gray-500">{t('pda.GlobalSearchPage.loading')}</div>
      )}
    </>
  );
};

export default function PdaGlobalSearchPage() {
  const { t } = useTranslation();
  
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2">
      <Header title={t('pda.GlobalSearchPage.title')} />
      <Pda />
      <PageBody />
    </div>
  );
}